import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NOTES_MAX, parseQuickAdd, slugifyTag, type IsoDate, type Tag, type Task, type User,
} from '@hestia/core';
import { api, stampOf } from '../api.js';
import { Sheet } from '../components/Sheet.js';
import { Avatar, Icon, icons } from '../ui.js';
import { longDate, relativeDay } from '../lang.js';
import { DateChips, MonthGrid } from './composer/DatePicker.js';
import { RepeatPanel, TagPanel, TimePanel } from './composer/panels.js';
import {
  applyParse, draftOfTask, emptyDraft, repeatLabel, ruleOfDraft, sameDraft,
  type Draft, type Field,
} from './composer/draft.js';

/** One screen for making a task and for changing one.
 *
 *  It used to be two, and they disagreed about what a task even was: the create
 *  screen had a mode toggle and five headed sections, all of them on screen at
 *  once and most of them below the fold on a phone; the edit screen could change
 *  a title and nothing else — not the date, which is the thing people actually
 *  come back to change.
 *
 *  The shape now is: what a task always has is on screen, and what it sometimes
 *  has is a chip you tap. Exactly one level of expansion, never two — a chip that
 *  opens a panel that opens another panel is how people lose track of where they
 *  are, and of what will be saved.
 *
 *  There is no Quick/Details toggle. A toggle is a mode, and a mode leaves people
 *  unsure whether the fields they cannot currently see are going to be saved.
 *  Typing is the quick path: the sentence you write fills the chips as you go. */

type Panel = 'date' | 'time' | 'repeat' | 'tags' | 'notes' | null;

interface Props {
  today: IsoDate;
  users: User[];
  tags: Tag[];
  onClose: () => void;
  onChanged: () => void;
  /** Present when changing a task, absent when making one. */
  taskId?: string;
}

export function TaskComposer({ today, users, tags, onClose, onChanged, taskId }: Props) {
  const editing = taskId !== undefined;

  const [base, setBase] = useState<Draft>(() => emptyDraft(today));
  const [panel, setPanel] = useState<Panel>(null);
  const [touched, setTouched] = useState<Set<Field>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Raw text while creating: the words stay exactly as typed and the tokens are
   *  stripped once, on save. Rewriting the field under someone's cursor as they
   *  type is the sort of help nobody asks for twice. */
  const [text, setText] = useState('');

  // ── editing an existing task ──────────────────────────────────────────
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(editing);
  const slugOf = useCallback(
    (ids: readonly string[]) => ids.map((id) => tags.find((t) => t.id === id)?.slug)
      .filter((s): s is string => !!s),
    [tags],
  );

  /** Read through a ref, not through the dependency list. Both of these change
   *  identity on every render of the app above, and saving calls back into it —
   *  so depending on them would make each save refetch the task and overwrite
   *  whatever had been typed since. The task is loaded once, per task. */
  const latest = useRef({ slugOf, onClose });
  latest.current = { slugOf, onClose };

  useEffect(() => {
    if (!taskId) return;
    let live = true;
    api.task(taskId).then((r) => {
      if (!live) return;
      setTask(r.task);
      setBase(draftOfTask(r.task, latest.current.slugOf(r.task.tagIds)));
      setLoading(false);
    }).catch(() => latest.current.onClose());
    return () => { live = false; };
  }, [taskId]);

  // ── what the sentence being typed means ───────────────────────────────
  const parsed = useMemo(
    () => (!editing && text.trim() ? parseQuickAdd(text, { today }) : null),
    [editing, text, today],
  );
  const draft = useMemo(
    () => (parsed ? applyParse(base, parsed, users, touched) : base),
    [base, parsed, users, touched],
  );

  /** Setting something by hand marks it, so the words being typed stop having an
   *  opinion about it. */
  const set = useCallback((patch: Partial<Draft>, fields: Field[] = []) => {
    setTouched((t) => (fields.length ? new Set([...t, ...fields]) : t));
    setBase((b) => {
      // The parse has to be folded in first, or setting a time would also throw
      // away the date the same sentence had already resolved.
      const from = parsed ? applyParse(b, parsed, users, touched) : b;
      return { ...from, ...patch };
    });
  }, [parsed, users, touched]);

  // ── saving ────────────────────────────────────────────────────────────
  const savedRef = useRef<Draft | null>(null);
  useEffect(() => { if (task && !savedRef.current) savedRef.current = draft; }, [task, draft]);

  /** Send only what actually changed.
   *
   *  Not an optimisation — a correctness rule. `tags` is resolved from ids the
   *  app has to already know about, so a composer opened before the tag list
   *  arrived would read the task as untagged; sending that whole draft back
   *  would then strip tags nobody touched. A patch that says nothing about a
   *  field leaves it exactly as it was. */
  const persist = useCallback(async (d: Draft) => {
    const was = savedRef.current;
    if (!task || !was || sameDraft(d, was)) return;
    savedRef.current = d;

    const patch: Parameters<typeof api.update>[1] = {};
    if (d.title.trim() && d.title !== was.title) patch.title = d.title.trim();
    if (d.notes !== was.notes) patch.notes = d.notes.trim() || null;
    if (d.date !== was.date) patch.startDate = d.date;
    if (d.endDate !== was.endDate) patch.endDate = d.endDate;
    if (d.time !== was.time) {
      patch.startTime = d.time;
      // A task with no time cannot have a length.
      if (!d.time) patch.durationMin = null;
    }
    if (d.time && d.durationMin !== was.durationMin) patch.durationMin = d.durationMin;
    if (d.assigneeId !== was.assigneeId) patch.assigneeId = d.assigneeId;
    if (d.repeat !== was.repeat || d.weekdays.join() !== was.weekdays.join()) {
      patch.rrule = ruleOfDraft(d, task.rrule);
    }
    if ([...d.tags].sort().join() !== [...was.tags].sort().join()) patch.tags = d.tags;
    if (!Object.keys(patch).length) return;

    const updated = await api.update(task.id, patch, stampOf(task));
    setTask(updated);
    onChanged();
  }, [task, onChanged]);

  // An edit saves itself. The overwhelming majority of visits here are one
  // change — a reschedule — and asking someone to confirm a thing they can see
  // has already happened is a button for its own sake.
  useEffect(() => {
    if (!editing || !task) return;
    const id = setTimeout(() => { void persist(draft).catch(() => setError('Could not save that.')); }, 500);
    return () => clearTimeout(id);
  }, [editing, task, draft, persist]);

  async function create() {
    if (busy || !draft.title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.create({
        title: draft.title.trim(),
        notes: draft.notes.trim() || null,
        startDate: draft.date,
        endDate: draft.endDate,
        startTime: draft.time,
        durationMin: draft.time ? draft.durationMin : null,
        assigneeId: draft.assigneeId,
        rrule: ruleOfDraft(draft, null),
        tags: draft.tags,
      });
      onChanged();
      onClose();
    } catch {
      setError('Could not add that. Try again in a moment.');
      setBusy(false);
    }
  }

  async function close() {
    if (editing) await persist(draft).catch(() => {});
    onClose();
  }

  if (loading) return <Sheet onClose={onClose}><div style={{ padding: 60 }} /></Sheet>;

  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? null : p));
  const tagNames = draft.tags.map((s) => tags.find((t) => t.slug === s)?.name ?? s);
  const repeat = repeatLabel(draft, task?.rrule ?? null);

  return (
    <Sheet onClose={close}>
      <header className="cmp-head">
        <button className="cmp-cancel" onClick={close}>{editing ? 'Done' : 'Cancel'}</button>
        <div style={{ flexGrow: 1 }} />
        {editing && (
          <button className="pill" aria-label="Delete task"
                  style={{ width: 38, height: 38, background: 'var(--soft)', color: 'var(--ink-2)' }}
                  onClick={async () => { await api.remove(task!.id, task!.title); onChanged(); onClose(); }}>
            <Icon path={icons.trash} size={17} />
          </button>
        )}
      </header>

      <div className="sheet-body cmp-body">
        <input className="input" autoFocus={!editing}
               value={editing ? draft.title : text}
               placeholder="What needs doing?"
               onChange={(e) => (editing ? set({ title: e.target.value }) : setText(e.target.value))}
               onKeyDown={(e) => { if (e.key === 'Enter' && !editing) { e.preventDefault(); void create(); } }} />

        {/* The one thing worth teaching, shown only while there is nothing to
            read instead. Most people never discover that the title field takes
            a whole sentence unless something says so. */}
        {!editing && !text.trim() && (
          <div className="dp-echo">Plain words work — “bins out every Mon at 7pm”.</div>
        )}

        {/* Always on screen: a task is a thing and a day. */}
        <DateChips value={draft.date} end={draft.endDate} today={today} open={panel === 'date'}
                   onPick={(d) => set({ date: d, endDate: null }, ['date'])}
                   onToggle={() => toggle('date')} />
        {panel === 'date' && (
          <MonthGrid value={draft.date} end={draft.endDate} today={today}
                     onPick={(d) => set({ date: d }, ['date'])}
                     onPickEnd={(d) => set(
                       // A span and a repeat cannot both be true, so choosing
                       // one puts the other back to nothing.
                       d ? { endDate: d, repeat: 'none', weekdays: [] } : { endDate: null },
                       ['date', 'repeat'])} />
        )}

        {/* Also always on screen: with two people in a household, who is doing it
            is the question the app exists to answer. Two avatars cost a row. */}
        <div className="chiprow">
          <button type="button" className={`qchip${draft.assigneeId === null ? ' on' : ''}`}
                  aria-pressed={draft.assigneeId === null}
                  onClick={() => set({ assigneeId: null }, ['assignee'])}>Anyone</button>
          {users.map((u) => (
            <button key={u.id} type="button" className={`qchip qchip-who${draft.assigneeId === u.id ? ' on' : ''}`}
                    aria-pressed={draft.assigneeId === u.id}
                    onClick={() => set({ assigneeId: draft.assigneeId === u.id ? null : u.id }, ['assignee'])}>
              <Avatar user={u} size={26} />{u.displayName}
            </button>
          ))}
        </div>

        {/* Everything a task only sometimes has. Set ones show what they say. */}
        <div className="chiprow cmp-more">
          <Attr on={!!draft.time} icon={icons.clock} open={panel === 'time'} onClick={() => toggle('time')}
                label={draft.time
                  ? draft.time + (draft.durationMin ? ` · ${draft.durationMin}m` : '')
                  : 'Time'} />
          {!draft.endDate && (
            <Attr on={draft.repeat !== 'none'} icon={icons.repeat} open={panel === 'repeat'}
                  onClick={() => toggle('repeat')} label={repeat || 'Repeat'} />
          )}
          <Attr on={draft.tags.length > 0} icon={icons.tag} open={panel === 'tags'}
                onClick={() => toggle('tags')}
                label={tagNames.length ? tagNames.join(', ') : 'Tags'} />
          <Attr on={!!draft.notes.trim()} icon={icons.note} open={panel === 'notes'}
                onClick={() => toggle('notes')} label="Notes" />
        </div>

        {panel === 'time' && <TimePanel d={draft} set={(p) => set(p, ['time', 'duration'])} />}
        {panel === 'repeat' && (
          <RepeatPanel d={draft} original={task?.rrule ?? null} set={(p) => set(p, ['repeat'])} />
        )}
        {panel === 'tags' && <TagPanel d={draft} tags={tags} set={(p) => set(p, ['tags'])} />}
        {(panel === 'notes' || draft.notes.trim()) && (
          <NotesField value={draft.notes} autoFocus={panel === 'notes'}
                      onChange={(notes) => set({ notes })} />
        )}

        {/* What the words will become, for the one case the chips cannot show:
            the title itself, once the dates and tags are taken out of it. */}
        {!editing && parsed && parsed.title !== text.trim() && (
          <div className="dp-echo">Saves as “{parsed.title}”</div>
        )}
        {!editing && parsed?.assigneeHint
          && !users.some((u) => u.username === parsed.assigneeHint) && (
          <div className="dp-echo">Nobody here is called “{parsed.assigneeHint}”</div>
        )}

        {error && <div style={{ fontSize: 13.5, color: 'var(--amber-on)' }}>{error}</div>}
      </div>

      {!editing && (
        <div className="cmp-foot">
          <button className="pill pill-accent" style={{ height: 54, width: '100%', fontSize: 16 }}
                  disabled={!draft.title.trim() || busy} onClick={create}>
            {busy ? 'Adding…' : 'Add task'}
          </button>
        </div>
      )}
      {editing && task && (
        <div className="cmp-foot cmp-foot-quiet">
          {draft.endDate
            ? `${longDate(draft.date)} → ${longDate(draft.endDate)}`
            : `${relativeDay(draft.date, today)} · ${longDate(draft.date)}`}
        </div>
      )}
    </Sheet>
  );
}

/** One of the sometimes-attributes: a chip that says what it holds, or invites
 *  you to give it something. */
function Attr({ on, icon, label, open, onClick }: {
  on: boolean; icon: string; label: string; open: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} aria-expanded={open}
            className={`attr${on ? ' set' : ''}${open ? ' open' : ''}`}>
      <Icon path={icon} size={15} />
      <span>{label}</span>
    </button>
  );
}


/** A task's notes: a line of reminder, or a whole email thread pasted in as
 *  context, or instructions for the agent — the same field has to be pleasant
 *  for all three. It starts at two lines and grows with what is in it, up to
 *  half the screen, and scrolls past that; a fixed two-row box with no resize
 *  handle made anything longer than a sentence a keyhole.
 *
 *  Notes are shown here and nowhere on the wall panel, which draws titles only. */
function NotesField({ value, autoFocus, onChange }: {
  value: string; autoFocus: boolean; onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);

  // Only mentioned once it matters: a counter on every note is noise, a
  // warning before a paste gets cut off is not.
  const near = value.length > NOTES_MAX * 0.9;
  return (
    <div>
      <textarea ref={ref} className="field-pill notes-field" rows={2} value={value}
                maxLength={NOTES_MAX} autoFocus={autoFocus}
                placeholder="Anything worth adding — context, links, how to do it"
                onChange={(e) => onChange(e.target.value)} />
      {near && (
        <div className="notes-count">
          {value.length.toLocaleString()} of {NOTES_MAX.toLocaleString()} characters
        </div>
      )}
    </div>
  );
}
